import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { CircleWithCheckIcon, RadioIcon, SendIcon } from '@modernloop/shared/icons';

import {
  Availability_ApplicationFragment,
  Availability_AvailabilityFragment,
  Availability_AvailabilityRequestFragment,
  Availability_CandidateFragment,
  Availability_InterviewPlanFragment,
  Availability_JobStageFragment,
  Availability_ScheduleFragment,
  Maybe,
  TaskStatus,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import { useIsOnCurrentAts } from 'src/hooks/atsService';
import { useFlag } from 'src/hooks/feature-flag';

import { CandidateAvailabilityOptions } from 'src/store/slices/candidate-availability-options';

import { getProvideAvailabilitySettings } from 'src/views-new/CandidateAvailabilityOptions/getProvideAvailabilitySettings';
import BaseCandidateAvailabilitySection from 'src/views-new/CandidateDetails/CandidateAvailabilitySection';
import { CandidateAvailabilityFragment } from 'src/views-new/CandidateDetails/useCandidateDetailsQueries';
import { getInclusionDays } from 'src/views-new/SelfSchedule/SelfScheduleOptions';

import { SECONDS_IN_A_DAY } from 'src/constants';

import RequirementContainer from '../Common/RequirementContainer';

type AvailabilityProps = {
  taskId: string;
  selfScheduleRequestId: string | undefined;
  status?: TaskStatus;
  availabilityRequestedAfterSchedule: boolean;
  availabilityReceivedAfterSchedule: boolean;
};

type AvailabilityFragmentProps = {
  // eslint-disable-next-line modernloop/component-with-fragments.cjs
  application: Availability_ApplicationFragment;
  availability?: Maybe<Availability_AvailabilityFragment>;
  availabilityRequest?: Maybe<Availability_AvailabilityRequestFragment>;
  candidate: Availability_CandidateFragment;
  jobStage: Availability_JobStageFragment;
  interviewPlan?: Maybe<Availability_InterviewPlanFragment>;
  schedule?: Maybe<Availability_ScheduleFragment>;
};

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const Availability: FCWithFragments<AvailabilityFragmentProps, AvailabilityProps> = ({
  taskId,
  application,
  availability,
  availabilityRequest,
  candidate,
  jobStage,
  interviewPlan,
  schedule,
  selfScheduleRequestId,
  status,
  availabilityRequestedAfterSchedule,
  availabilityReceivedAfterSchedule,
}) => {
  const { t } = useTranslation();
  const isCurrentAts = useIsOnCurrentAts(candidate.atsType || undefined);
  const collaborationLinkEnabled = useFlag('prd-155-codesignal-template-select');

  const icon = useMemo((): React.ReactNode => {
    if (availabilityRequest?.externalId) {
      if (availabilityReceivedAfterSchedule) {
        return <CircleWithCheckIcon color="success" />;
      }
      if (availabilityRequestedAfterSchedule) {
        return <SendIcon color="warning" />;
      }
      return availability?.candidateEnteredAvailability?.length || availability?.rcEnteredAvailability?.length ? (
        <CircleWithCheckIcon color="success" />
      ) : (
        <SendIcon color="warning" />
      );
    }
    if (schedule?.id || status === TaskStatus.ReadyToSchedule) {
      return <CircleWithCheckIcon color="success" />;
    }
    return <RadioIcon />;
  }, [
    availability?.candidateEnteredAvailability?.length,
    availability?.rcEnteredAvailability?.length,
    availabilityRequest?.externalId,
    schedule?.id,
    status,
    availabilityRequestedAfterSchedule,
    availabilityReceivedAfterSchedule,
  ]);

  const availabilityConfig = getProvideAvailabilitySettings(
    {
      jobStage: interviewPlan,
      jobStageSettingsV2: jobStage?.jobStageSettingsV2,
    },
    { collaborationLinkEnabled }
  );

  const hasAvailability =
    availability?.candidateEnteredAvailability?.length || availability?.rcEnteredAvailability?.length;

  const requirementTitle = useMemo(() => {
    if (schedule?.id && !availabilityRequestedAfterSchedule && !hasAvailability) {
      return t('hiring-dashboard-tasks:tasks.availability.collectTitleSkipped');
    }

    return t('hiring-dashboard-tasks:tasks.availability.collectTitle');
  }, [hasAvailability, schedule?.id, availabilityRequestedAfterSchedule, t]);

  const getOptions = () => {
    const jobStageSettingsV2 = jobStage?.jobStageSettingsV2;

    const rollingDays = () => {
      if (jobStageSettingsV2?.candidateAvailabilitySettingsV2?.useRollingDays.value) {
        return jobStageSettingsV2?.candidateAvailabilitySettingsV2?.timeframe.duration.seconds / SECONDS_IN_A_DAY;
      }
      return undefined;
    };
    return {
      taskId,
      jobStageId: jobStage.id,
      numberOfDays: availabilityConfig.numberOfDays,
      minutesPerDays: availabilityConfig.minutesPerDays,
      minimumTimeBlockMinutes: availabilityConfig.minimumTimeBlockMinutes,
      advanceNoticeMinutes: jobStageSettingsV2?.candidateAvailabilitySettingsV2?.advanceNotice.duration.seconds / 60,
      candidateNote: jobStageSettingsV2?.candidateAvailabilitySettingsV2?.candidateNote.value || '',
      timezone: candidate.timezone || getLocalTimezone(),
      shouldRespectLoadLimit:
        jobStageSettingsV2?.candidateAvailabilitySettingsV2?.shouldRespectLoadLimit.value || false,
      bookIfPerfectScheduleFound:
        jobStageSettingsV2?.candidateAvailabilitySettingsV2?.bookIfPerfectScheduleFound.value || false,
      canScheduleOverRecruitingKeywords:
        jobStageSettingsV2?.candidateAvailabilitySettingsV2?.canScheduleOverRecruitingKeywords.value || false,
      canScheduleOverAvailableKeywords:
        jobStageSettingsV2?.candidateAvailabilitySettingsV2?.canScheduleOverAvailableKeywords.value || false,
      canScheduleOverFreeTime:
        jobStageSettingsV2?.candidateAvailabilitySettingsV2?.canScheduleOverFreeTime.value || false,
      rollingDays: rollingDays(),
      inclusionDays: !rollingDays()
        ? getInclusionDays(
            jobStageSettingsV2?.candidateAvailabilitySettingsV2?.timeframe.duration.seconds / SECONDS_IN_A_DAY
          )
        : undefined,
      applicationId: application.id,
      timeframeNumberOfDays:
        jobStageSettingsV2?.candidateAvailabilitySettingsV2?.timeframe.duration.seconds / SECONDS_IN_A_DAY,
      useRollingDays: jobStageSettingsV2?.candidateAvailabilitySettingsV2?.useRollingDays.value,
    } as CandidateAvailabilityOptions;
  };

  return (
    <RequirementContainer title={requirementTitle} icon={icon}>
      <BaseCandidateAvailabilitySection
        jobStageSettingsV2={jobStage?.jobStageSettingsV2 || undefined}
        taskId={taskId}
        application={application}
        availabilityRequest={availabilityRequest}
        applicationId={application.id}
        jobId={application?.jobStage?.job?.id}
        candidateId={candidate.id}
        jobStageName={jobStage.name}
        jobStageId={jobStage.id}
        interviewPlanId={interviewPlan?.id}
        candidateTimezone={candidate.timezone || undefined}
        lastApplicationStageCreatedAt={schedule?.createdAt}
        selfScheduleRequestId={selfScheduleRequestId}
        isCurrentAts={isCurrentAts}
        readonly={Boolean(schedule?.id)}
        availability={availability || undefined}
        options={
          availabilityRequest
            ? {
                id: availabilityRequest.id,
                applicationId: availabilityRequest.applicationId,
                jobStageId: availabilityRequest.jobStageId,
                taskId,
                numberOfDays: availabilityRequest.numberOfDays,
                minutesPerDays: availabilityRequest.minutesPerDays,
                minimumTimeBlockMinutes: availabilityRequest.minimumTimeBlockMinutes,
                timezone: availabilityRequest.timezone,
                availabilityStartDate: availabilityRequest.availabilityStartDate,
                availabilityEndDate: availabilityRequest.availabilityEndDate,
                advanceNoticeMinutes: availabilityRequest.advanceNoticeMinutes,
                candidateNote: availabilityRequest.candidateNote || undefined,
                externalId: availabilityRequest.externalId,
                createdAt: availabilityRequest.createdAt,
                updatedAt: availabilityRequest.updatedAt,
                lastCandidateCommunicationSentAt: availabilityRequest.lastCandidateCommunicationSentAt,
                creator: availabilityRequest.creator?.fullName || undefined,
                shouldRespectLoadLimit: availabilityRequest.shouldRespectLoadLimit,
                bookIfPerfectScheduleFound: availabilityRequest.bookIfPerfectScheduleFound,
                // This is needed so the upsert will use the boolean values below and not default to false
                isValidInterviewPlan: true,
                canScheduleOverAvailableKeywords: availabilityRequest.suggestOverAvailableKeywords,
                canScheduleOverFreeTime: availabilityRequest.suggestOverFreeTime,
                canScheduleOverRecruitingKeywords: availabilityRequest.suggestOverRecruitingKeywords,
                useRollingDays: Boolean(availabilityRequest.rollingDays),
                timeframeNumberOfDays: availabilityRequest.rollingDays,
                inclusionDays: availabilityRequest.inclusionDays,
              }
            : getOptions()
        }
        availabilityRequestedAfterSchedule={availabilityRequestedAfterSchedule}
        availabilityReceivedAfterSchedule={availabilityReceivedAfterSchedule}
      />
    </RequirementContainer>
  );
};

Availability.fragments = {
  application: gql`
    ${BaseCandidateAvailabilitySection.fragments.application}
    fragment Availability_application on Application {
      id
      jobStage {
        id
        job {
          id
        }
      }
      ...BaseCandidateAvailabilitySection_application
    }
  `,
  availability: gql`
    ${CandidateAvailabilityFragment}
    ${BaseCandidateAvailabilitySection.fragments.availability}
    fragment Availability_availability on CandidateAvailability {
      ...CandidateAvailabilityFragment
      ...BaseCandidateAvailabilitySection_availability
    }
  `,
  availabilityRequest: gql`
    ${BaseCandidateAvailabilitySection.fragments.availabilityRequest}
    fragment Availability_availabilityRequest on CandidateAvailabilityOptions {
      id
      applicationId
      availabilityEndDate
      availabilityStartDate
      candidateNote
      externalId
      createdAt
      jobStageId
      minimumTimeBlockMinutes
      minutesPerDays
      numberOfDays
      timezone
      updatedAt
      lastCandidateCommunicationSentAt
      advanceNoticeMinutes
      creator {
        fullName
      }
      shouldRespectLoadLimit
      bookIfPerfectScheduleFound
      suggestOverRecruitingKeywords
      suggestOverAvailableKeywords
      suggestOverFreeTime
      rollingDays
      inclusionDays
      ...BaseCandidateAvailabilitySection_availabilityRequest
    }
  `,
  candidate: gql`
    fragment Availability_candidate on Candidate {
      id
      timezone
      atsType
    }
  `,
  jobStage: gql`
    ${BaseCandidateAvailabilitySection.fragments.jobStageSettingsV2}
    ${getProvideAvailabilitySettings.fragments.jobStageSettingsV2}
    fragment Availability_jobStage on JobStage {
      id
      name
      jobStageSettingsV2 {
        ...getProvideAvailabilitySettings_JobStageSettingsV2
        ...BaseCandidateAvailabilitySection_JobStageSettingsV2
      }
    }
  `,
  interviewPlan: gql`
    ${getProvideAvailabilitySettings.fragments.jobStage}
    fragment Availability_interviewPlan on JobStage {
      id
      ...getProvideAvailabilitySettings_JobStage
      jobStageInterviewGroups {
        id
        jobStageInterviews {
          id
          interviewType
          duration
        }
      }
    }
  `,
  schedule: gql`
    fragment Availability_schedule on ApplicationStage {
      id
      createdAt
    }
  `,
};

export default Availability;
